// import moment from 'moment';
import Head from 'next/head';
import Router from 'next/router';
// import Link from 'next/link';

import React from 'react';
import Layout from '../components/layout';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';

import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import TablePagination from '@mui/material/TablePagination';

import { getCompaniesApiMethod } from '../lib/api';
import TextField from '@mui/material/TextField';

const dev = process.env.NODE_ENV !== 'production';
const URL_WIB = dev ? process.env.NEXT_PUBLIC_URL_WIB : process.env.NEXT_PUBLIC_PRODUCTION_URL_WIB;

type Company = {
  _id: string;
  name: string;
  logo: string;
  note: string;
  employeeRange: string;
  fundingRange: string;
  location: string;
  foundationYear: string;
  websiteUrl: string;
  jobBoardUrl: string;
  scriptName: string;
  scriptId: string;
  companySlug: string;
  funding: string;
  yearFounded: string;
  city: string;
  state: string;
  therArea: string;
  drugTypes: string;
  drugNotes: string;
  clinicalStage: string;
  business: string;
  investors: string;
};

type Props = {
  isThemeDark: boolean;
  currentRoute: string;
  companies: Company[];
  error: string;
  numberOfCompanies: number;
  pageNumber: number;
  isDataLoadedProp: boolean;
  search: string;
};

type State = {
  showHomepageModal: boolean;
  page: number;
  rowsPerPage: number;
  isDataLoaded: boolean;
  search?: string;
};

class CompaniesPage extends React.Component<Props, State> {
  public state = {
    showHomepageModal: false,
    page: this.props.pageNumber || 0,
    rowsPerPage: 50,
    isDataLoaded: this.props.isDataLoadedProp ? true : false,
    search: this.props.search || '',
  };

  public static async getInitialProps(ctx) {
    // console.log('CompaniesPage.getInitialProps');

    const { page, search } = ctx.query;

    const pageNumber = parseInt(page as string, 10);

    const data = await getCompaniesApiMethod(
      {
        page: page === undefined ? 0 : pageNumber - 1,
        search,
      },
      ctx.req ? ctx.req.headers : {},
    );

    if (data && !data.error) {
      return {
        companies: data.companies,
        numberOfCompanies: data.numberOfCompanies,
        pageNumber: page === undefined ? 0 : pageNumber - 1,
        isDataLoadedProp: true,
        search,
      };
    } else {
      return { error: data.error };
    }
  }

  public componentDidUpdate(prevProps: Props) {
    // console.log('CompaniesPage.componentDidUpdate');

    const { pageNumber, isDataLoadedProp } = this.props;

    // console.log(pageNumber, prevProps.pageNumber, isDataLoadedProp);

    if (prevProps.pageNumber !== pageNumber) {
      this.setState({ page: pageNumber, isDataLoaded: isDataLoadedProp });
    }
  }

  private handleSearch = () => {
    const { search } = this.state;
    Router.push(`/companies?search=${search}`);
  };

  public render() {
    const { companies, error, numberOfCompanies } = this.props;
    const { page, rowsPerPage, isDataLoaded, search } = this.state;

    // console.log(page);

    return (
      <Layout {...this.props}>
        <div style={{ textAlign: 'center', margin: '0 20px' }}>
          <Head>
            <title>Companies | Work in biotech</title>
            <meta name="description" content="List of all biotech startups at workinbiotech.com" />
            <meta name="robots" content="index, follow" />
          </Head>
          <p />
          <br />
          <h1>Companies</h1>

          <p>
            Our website lists jobs from <b>{numberOfCompanies}</b> biotech startups.
          </p>
          <p />
          <br />
          <Button
            href="https://forms.gle/7D3sMdjSxxWicuMT7"
            target="_blank"
            rel="noopener noreferrer"
            component="a"
            variant="contained"
            color="success"
          >
            Add your company
          </Button>
          <p />
          <br />
          <br />
          <Box
            sx={{
              display: { xs: 'flex', md: 'inline-flex' },
              verticalAlign: 'middle',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 0 },
            }}
          >
            <TextField
              label="Company name"
              sx={{ float: 'left', width: { xs: '100%', md: '400px' } }}
              value={search}
              onKeyDown={(e) => {
                if (e.keyCode === 13) {
                  this.handleSearch();
                }
              }}
              onChange={(e) => this.setState({ search: e.target.value })}
            />

            <Button
              onClick={this.handleSearch}
              sx={{ float: 'left', marginLeft: { xs: 0, md: '15px' } }}
              variant="contained"
              size="large"
              color="primary"
            >
              Search by company name
            </Button>
          </Box>
          <p />
          <div>
            <TablePagination
              component="div"
              count={numberOfCompanies}
              page={page}
              onPageChange={this.handleChangePageForCompaniesPage}
              rowsPerPage={rowsPerPage}
              labelRowsPerPage="Companies per page"
              rowsPerPageOptions={[50]}
              style={{ display: 'inline-flex' }}
            />
          </div>
          <p />
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              marginTop: '10px',
              overflowX: 'auto',
            }}
          >
            <Table
              sx={{
                background: '#e9e9e9',
                margin: '0px auto',
                width: '96%',
                minWidth: '340px',
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell style={{ textAlign: 'center', width: '240px' }}>
                    <b>Company</b> <p /> (click to see more info)
                  </TableCell>
                  <TableCell>
                    <b>Short description</b>
                  </TableCell>
                  <TableCell>
                    <b>Founded</b>
                  </TableCell>
                  <TableCell>
                    <b>Location</b>
                  </TableCell>
                  <TableCell>
                    <b>Clinical stage</b>
                  </TableCell>
                  <TableCell>
                    <b>Therapy area</b>
                  </TableCell>
                  <TableCell>
                    <b>Drug types</b>
                  </TableCell>
                  <TableCell style={{ textAlign: 'center' }}>
                    <b>Drug notes</b>
                  </TableCell>
                  <TableCell>
                    <b>Funding</b>
                  </TableCell>
                  <TableCell>
                    <b>Investors</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isDataLoaded &&
                  companies &&
                  companies.length > 0 &&
                  companies.map((c, i) => (
                    <TableRow key={c._id}>
                      <TableCell>
                        {page * 50 + i + 1}.{' '}
                        <a
                          href={`${URL_WIB}/companies/${c.companySlug}`}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                        >
                          {c.name}{' '}
                        </a>{' '}
                      </TableCell>
                      <TableCell>{c.business}</TableCell>
                      <TableCell>{c.yearFounded}</TableCell>
                      <TableCell>{c.city ? c.city + ', ' + c.state : ''}</TableCell>
                      <TableCell>{c.clinicalStage}</TableCell>
                      <TableCell>{c.therArea}</TableCell>
                      <TableCell>{c.drugTypes}</TableCell>
                      <TableCell>{c.drugNotes}</TableCell>
                      <TableCell>{c.funding}</TableCell>
                      <TableCell>{c.investors}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Grid>
          <p />
          <TablePagination
            component="div"
            count={numberOfCompanies}
            page={page}
            onPageChange={this.handleChangePageForCompaniesPage}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage="Companies per page"
            rowsPerPageOptions={[50]}
            style={{ display: 'inline-flex' }}
          />
          <p />
          <h3
            style={{
              textAlign: 'center',
            }}
          >
            {error ? error : null}
          </h3>
        </div>
        <p
          style={{
            marginBottom: '20%',
          }}
        />
        {/* <HomepageModal
          open={this.state.showHomepageModal}
          onClose={this.handleHomepageModalClose}
        /> */}
      </Layout>
    );
  }

  // private handleHomepageModalClose = () => {
  //   this.setState({ showHomepageModal: false });
  // };

  // private showHomepageModal = () => {
  //   this.setState({ showHomepageModal: true });
  // };

  private handleChangePageForCompaniesPage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    event.preventDefault();

    const { search } = this.state;

    Router.push(`/companies?search=${search}&page=${newPage + 1}`);

    this.setState({ page: newPage, isDataLoaded: false });
  };
}

export default CompaniesPage;
